import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ChevronUp, ChevronDown, Shield, BarChart3 } from 'lucide-react';
import usePageContent from '@/hooks/usePageContent';

const CONSENT_KEY = 'cookie_consent';
const GA_TRACKING_ID = 'G-2N2WYBJF4J';

// Load Google Analytics dynamically
const loadGoogleAnalytics = () => {
  if (document.getElementById('ga-script')) return;
  
  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);
  
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_TRACKING_ID, {
    anonymize_ip: true
  });
};

// Remove Google Analytics
const removeGoogleAnalytics = () => {
  const script = document.getElementById('ga-script');
  if (script) script.remove();
  window.dataLayer = [];
  
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const [name] = cookie.split('=');
    if (name.trim().startsWith('_ga') || name.trim().startsWith('_gid')) {
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
};

export const getConsentStatus = () => {
  const consent = localStorage.getItem(CONSENT_KEY);
  if (!consent) return null;
  try {
    return JSON.parse(consent);
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = () => {
  const consent = getConsentStatus();
  return consent?.analytics === true;
};

// Function to reopen cookie settings
export const reopenCookieSettings = () => {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
};

const CookieConsent = () => {
  const { t: c } = usePageContent('cookie');
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
  });

  useEffect(() => {
    const consent = getConsentStatus();
    
    if (consent === null) {
      setShowBanner(true);
    } else {
      if (consent.analytics) {
        loadGoogleAnalytics();
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    loadGoogleAnalytics();
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    removeGoogleAnalytics();
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    
    if (preferences.analytics) {
      loadGoogleAnalytics();
    } else {
      removeGoogleAnalytics();
    }
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-300"
      data-testid="cookie-consent-banner"
    >
      {/* Details Panel (slides up when expanded) */}
      {showDetails && (
        <div className="bg-white border-t border-slate-200 shadow-lg">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Necessary Cookies */}
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-slate-900 text-sm">{c('notwendig_title', 'Notwendige Cookies')}</h3>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap">Immer aktiv</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Erforderlich für Grundfunktionen der Website.
                  </p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-slate-900 text-sm">{c('analyse_title', 'Analyse-Cookies')}</h3>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                        className="sr-only peer"
                        data-testid="analytics-toggle"
                      />
                      <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Google Analytics zur Verbesserung der Website.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Banner Bar */}
      <div className="bg-slate-900 text-white shadow-2xl">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Text */}
            <div className="flex items-center gap-3 text-center sm:text-left">
              <Cookie className="w-5 h-5 text-blue-400 flex-shrink-0 hidden sm:block" />
              <p className="text-sm text-slate-300">
                {c('banner_text', 'Wir verwenden Cookies für ein optimales Website-Erlebnis.')}{' '}
                <Link to="/datenschutz" className="text-blue-400 hover:text-blue-300 underline">
                  Mehr erfahren
                </Link>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Details Toggle */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1"
                data-testid="cookie-details-toggle"
              >
                Einstellungen
                {showDetails ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </button>

              {/* Reject Button */}
              <button
                onClick={handleRejectAll}
                className="px-4 py-1.5 text-sm border border-slate-600 text-slate-300 hover:bg-slate-800 rounded-lg transition-colors"
                data-testid="cookie-reject-btn"
              >
                Ablehnen
              </button>

              {/* Accept/Save Button */}
              {showDetails ? (
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                  data-testid="cookie-save-preferences-btn"
                >
                  Speichern
                </button>
              ) : (
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                  data-testid="cookie-accept-btn"
                >
                  Akzeptieren
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
